import React from 'react';
import { Link } from 'react-router-dom';
import { useSeoHead } from '../hooks/useSeoHead';
import { GuideLanguageSwitch } from '../components/svs-guide/GuideLanguageSwitch';

// Single flag controlling whether this noindex, unlinked knowledge-base page
// is allowed to be indexed. Flip to `true` (and mirror it in
// prerenderRoutes.ts for this route) when the page is ready to go public —
// that is the only change needed.
const INDEXABLE = false;

const PATH = '/de/guides/selbststaendig-werden/';
const CANONICAL = `https://pstax.at${PATH}`;
const TITLE = 'Selbstständig werden in Österreich: Der komplette Weg (2026) | PSTax';
const DESCRIPTION =
  'Welche Anmeldungen du brauchst, in welcher Reihenfolge, was dein erstes Jahr wirklich kostet — und die eine Frage, die vorher geklärt sein muss. Ein verständlicher Leitfaden für Freelancer in Österreich.';

const SVS_GUIDE_PATH = '/de/guides/svs-sozialversicherung/';
// Neither the year-end-check guide nor the income-tax guide has a German
// version yet (only SvsYearEndCheckGuide.tsx, English-only, exists — see
// StartingOutSelfEmployedGuide.tsx for the income-tax situation, which is
// missing in both languages). Rather than send a German reader into an
// English-only page mid-article, both render as "Bald" placeholders here,
// same treatment the DE SVS guide already gives the missing income-tax page.

const TOC_ITEMS: { label: string; id: string }[] = [
  { label: 'Darfst du hier überhaupt arbeiten?', id: 'aufenthalt' },
  { label: 'Welche Form der Selbstständigkeit passt zu dir?', id: 'welche-form' },
  { label: 'Die Anmeldungen, in der richtigen Reihenfolge', id: 'schritte' },
  { label: 'Die Förderung, die die meisten Gründer verpassen', id: 'neufoeg' },
  { label: 'Was dein erstes Jahr wirklich kostet', id: 'erstes-jahr' },
  { label: 'Brauchst du eine:n Steuerberater:in?', id: 'steuerberater' },
  { label: 'Häufige Fragen', id: 'faq' },
  { label: 'Offizielle Quellen', id: 'quellen' },
];

const AT_A_GLANCE: [string, string][] = [
  ['Zuerst klären', 'Darfst du hier selbstständig sein?'],
  ['Zwei Wege', 'Gewerbeschein oder Neue Selbständige'],
  ['Wo du dich anmeldest', 'Bezirksbehörde, Finanzamt, SVS'],
  ['Frist', 'Finanzamt innerhalb eines Monats informieren'],
  ['Umsatzsteuerfrei bis', '55.000 € Umsatz'],
  ['Zurücklegen', 'Etwa ein Drittel deines Gewinns'],
];

const STEPS: { title: string; body: React.ReactNode }[] = [
  {
    title: 'Form klären',
    body: (
      <>
        Gewerbeschein oder <span lang="de">Neue:r Selbständige:r</span>, freies oder reglementiertes Gewerbe. Der
        Gründerservice der Wirtschaftskammer bestätigt dir das kostenlos.
      </>
    ),
  },
  {
    title: 'Gewerbe anmelden — oder eben nicht',
    body: (
      <>
        Mit Gewerbeschein: bei der <span lang="de">Bezirkshauptmannschaft</span> oder im Magistrat, oder online über
        das Unternehmensserviceportal. Die Anmeldung selbst ist kostenlos. Ohne Gewerbeschein: hier gibt es nichts
        anzumelden.
      </>
    ),
  },
  {
    title: 'Beim Finanzamt melden',
    body: (
      <>
        Innerhalb eines Monats nach Tätigkeitsbeginn. Du füllst einen Fragebogen zu deinem erwarteten Einkommen aus
        und erhältst deine Steuernummer. Das läuft getrennt von jeder anderen Anmeldung — das macht niemand für
        dich.
      </>
    ),
  },
  {
    title: 'SVS erledigen',
    body: (
      <>
        Mit Gewerbeschein passiert das automatisch mit der Gewerbeanmeldung. Als{' '}
        <span lang="de">Neue:r Selbständige:r</span> meldest du dich selbst, innerhalb eines Monats nach Beginn.
      </>
    ),
  },
  {
    title: 'Umsatzsteuer entscheiden',
    body: (
      <>
        Bis 55.000 € Umsatz im Jahr 2026 kannst du als Kleinunternehmer:in umsatzsteuerbefreit bleiben: keine
        Umsatzsteuer auf deinen Rechnungen, aber auch keine Vorsteuer zurück. Darüber, oder wenn du freiwillig
        optierst, brauchst du eine UID-Nummer und musst Voranmeldungen abgeben.
      </>
    ),
  },
];

// Exported so prerenderRoutes.ts can build the FAQPage JSON-LD from the same
// source instead of duplicating all four answers as a second copy of the text.
export const FAQS_SELBSTSTAENDIG_WERDEN: { q: string; a: React.ReactNode; aPlain: string }[] = [
  {
    q: 'Darf ich schon starten, bevor alle Anmeldungen erledigt sind?',
    aPlain:
      'Mit einem freien Gewerbe darfst du in der Regel loslegen, sobald die Anmeldung eingereicht ist. Aber "in der Regel" hat es in sich — bei reglementierten Gewerben kann vorher eine Entscheidung nötig sein, und die Fristen von Finanzamt und SVS laufen ab dem Tag, an dem du tatsächlich beginnst, nicht ab dem Tag, an dem du zur Anmeldung kommst. Erst anmelden, dann fakturieren.',
    a: (
      <>
        Mit einem freien Gewerbe darfst du in der Regel loslegen, sobald die Anmeldung eingereicht ist. Aber "in der
        Regel" hat es in sich — bei reglementierten Gewerben kann vorher eine Entscheidung nötig sein, und die
        Fristen von Finanzamt und SVS laufen ab dem Tag, an dem du tatsächlich beginnst, nicht ab dem Tag, an dem du
        zur Anmeldung kommst. Erst anmelden, dann fakturieren.
      </>
    ),
  },
  {
    q: 'Was kostet die Anmeldung?',
    aPlain:
      'Die Gewerbeanmeldung selbst ist kostenlos. Was von Tag eins an kostet, ist die SVS — 160,81 € im Monat im Jahr 2026 mit Gewerbeschein, unabhängig vom Umsatz. Plane das ein, bevor du irgendetwas anderes einplanst.',
    a: (
      <>
        Die Gewerbeanmeldung selbst ist kostenlos. Was von Tag eins an kostet, ist die SVS — 160,81 € im Monat im
        Jahr 2026 mit Gewerbeschein, unabhängig vom Umsatz. Plane das ein, bevor du irgendetwas anderes einplanst.
      </>
    ),
  },
  {
    q: 'Brauche ich ein Geschäftskonto?',
    aPlain:
      'Rechtlich nicht, als Einzelunternehmer:in. In der Praxis macht es die Buchhaltung deutlich einfacher, und es ist der Unterschied zwischen einem Abend und einem ganzen Wochenende zum Jahresende. Ein eigenes Konto für das Geld, das du für Steuer und SVS zurücklegst, ist noch mehr wert.',
    a: (
      <>
        Rechtlich nicht, als Einzelunternehmer:in. In der Praxis macht es die Buchhaltung deutlich einfacher, und es
        ist der Unterschied zwischen einem Abend und einem ganzen Wochenende zum Jahresende. Ein eigenes Konto für
        das Geld, das du für Steuer und SVS zurücklegst, ist noch mehr wert.
      </>
    ),
  },
  {
    q: 'Muss ich für all das Deutsch können?',
    aPlain:
      'Um es selbst zu machen, realistisch gesehen: ja — die Formulare, die Portale und jedes Schreiben sind auf Deutsch. Mit einer Steuerberatung an deiner Seite: nein. Du leitest weiter, was ankommt, und bekommst die Erklärung, wie du sie brauchst.',
    a: (
      <>
        Um es selbst zu machen, realistisch gesehen: ja — die Formulare, die Portale und jedes Schreiben sind auf
        Deutsch. Mit einer Steuerberatung an deiner Seite: nein. Du leitest weiter, was ankommt, und bekommst die
        Erklärung, wie du sie brauchst.
      </>
    ),
  },
];

const ExternalLink: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({
  href,
  children,
  className = '',
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener"
    className={`motion-reduce:transition-none ${className}`}
  >
    {children}
    <span aria-hidden="true"> ↗</span>
    <span className="sr-only">, öffnet in neuem Tab</span>
  </a>
);

const Callout: React.FC<{ tone: 'blue' | 'orange'; label: string; children: React.ReactNode }> = ({
  tone,
  label,
  children,
}) => (
  <div
    className={`my-6 rounded-2xl border-l-4 p-5 ${
      tone === 'blue' ? 'bg-pstax-blue/10 border-pstax-blue' : 'bg-pstax-orange/10 border-pstax-orange'
    }`}
  >
    <p className="text-[11px] font-black uppercase tracking-wider mb-2 text-pstax-navy">{label}</p>
    <p className="text-pstax-navy leading-relaxed">{children}</p>
  </div>
);

const SoonBadge: React.FC = () => (
  <span className="inline-flex items-center text-[10px] font-black uppercase tracking-wider text-pstax-gray bg-pstax-border/60 rounded-full px-2 py-0.5 ml-2 align-middle">
    Bald
  </span>
);

// The one new component this page introduces — a low-key partner referral,
// deliberately not styled like the CTA block so it reads as a pointer, not
// an ad. Same component as the English sibling, German copy.
const PartnerCard: React.FC = () => (
  <div className="my-10 rounded-2xl border-[1.5px] border-[#2f5f9e] bg-[#eef4fc] px-[26px] py-[28px]">
    <p className="text-[11px] font-black uppercase tracking-[0.11em] text-[#2f5f9e] mb-3">
      Wenn du bei null anfängst in Österreich
    </p>
    <h3 className="text-xl font-display font-bold text-pstax-navy mb-3">Setup in Austria</h3>
    <p className="mb-4 leading-relaxed text-pstax-navy">
      Für Menschen, die gerade erst hierher gezogen sind und die gesamte Gründung aus einer Hand erledigt haben
      wollen, bieten wir gemeinsam mit Miglena Hofer einen eigenen Service an: Gewerbeanmeldung und steuerliche
      Ersteinrichtung in einem Paket, vom Gewerbeschein über die SVS bis zur Anmeldung beim Finanzamt, der
      UID-Nummer und der ersten Steuererklärung.
    </p>
    <p className="mb-5 leading-relaxed text-pstax-navy">
      Den gibt es, weil "welches Amt zuerst" ein wirklich schlechter erster Eindruck von einem neuen Land ist — und
      weil die falsche Reihenfolge dich Zeit kostet, die du ohnehin schon knapp hast, während du nebenbei noch eine
      Wohnung suchst.
    </p>
    <a
      href="https://setupinaustria.at/"
      target="_blank"
      rel="noopener"
      className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-full bg-[#2f5f9e] text-white font-semibold px-6 py-3 hover:bg-[#254c7e] transition-all active:scale-95 motion-reduce:transition-none"
    >
      setupinaustria.at besuchen
      <span aria-hidden="true">↗</span>
      <span className="sr-only">, öffnet in neuem Tab</span>
    </a>
  </div>
);

export const SelbststaendigWerdenGuide: React.FC = () => {
  useSeoHead({
    title: TITLE,
    description: DESCRIPTION,
    canonical: CANONICAL,
    indexable: INDEXABLE,
    htmlLang: 'de',
    hreflang: [
      { lang: 'en', href: 'https://pstax.at/en/guides/starting-out-self-employed/' },
      { lang: 'de', href: 'https://pstax.at/de/guides/selbststaendig-werden/' },
      { lang: 'x-default', href: 'https://pstax.at/en/guides/starting-out-self-employed/' },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Selbstständig werden in Österreich',
        datePublished: '2026-09-17',
        dateModified: '2026-09-17',
        inLanguage: 'de',
        author: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo' },
        publisher: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo' },
        about: 'Selbstständigkeit in Österreich',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS_SELBSTSTAENDIG_WERDEN.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.aPlain },
        })),
      },
    ],
  });

  return (
    <div className="starting-out-guide pt-28 pb-24">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="text-sm text-pstax-gray mb-6">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/de/" className="hover:text-pstax-link">
                Startseite
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>Wissensdatenbank</li>
            <li aria-hidden="true">/</li>
            <li className="text-pstax-navy font-semibold" aria-current="page">
              Starten
            </li>
          </ol>
        </nav>

          <article className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-pstax-blue mb-4">
              Wissensdatenbank · Erste Schritte
            </p>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-pstax-navy mb-6 leading-tight">
              Selbstständig werden in Österreich
            </h1>
            <p className="text-xl text-pstax-gray leading-relaxed mb-4">
              Fünf Anmeldungen, in einer bestimmten Reihenfolge, und eine Frage, die vor allem anderen geklärt sein
              muss. Hier ist der ganze Weg auf einer Seite — und wo du weiterliest, sobald du mittendrin bist.
            </p>
            <p className="text-sm text-pstax-gray mb-4">Zahlen für 2026 · Zuletzt aktualisiert im September 2026</p>
            <GuideLanguageSwitch from={PATH} targetLang="EN" label="This page in English →" />

            {/* At a glance */}
            <div className="mt-10 mb-10 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8">
              <h2 className="text-lg font-display font-bold text-pstax-navy mb-4">Der Einstieg auf einen Blick</h2>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {AT_A_GLANCE.map(([label, value]) => (
                  <div
                    key={label}
                    className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm"
                  >
                    <dt className="text-pstax-gray">{label}</dt>
                    <dd className="font-semibold text-pstax-navy text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Calculator promo */}
            <div className="mb-10 bg-pstax-blue/10 border border-pstax-blue/30 rounded-2xl p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-black uppercase tracking-wider text-pstax-navy mb-1">
                  Deine Zahlen schätzen
                </p>
                <p className="text-sm text-pstax-gray">
                  Sieh dir an, was Einkommensteuer und SVS im ersten Jahr ungefähr von deinem Umsatz nehmen.
                </p>
              </div>
              <Link
                to="/de/#calculator"
                className="btn-outline shrink-0 min-h-[44px] flex items-center justify-center whitespace-nowrap motion-reduce:transition-none"
              >
                Zum Rechner
              </Link>
            </div>

            {/* Table of contents */}
            <nav className="toc-top mb-12" aria-label="Auf dieser Seite">
              <p className="text-[11px] font-black uppercase tracking-wider text-pstax-gray mb-3">Auf dieser Seite</p>
              <ol className="grid min-[620px]:grid-cols-2 gap-x-8 gap-y-1">
                {TOC_ITEMS.map((item, i) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="flex min-h-[32px] items-center text-sm text-pstax-navy hover:text-pstax-link py-1 motion-reduce:transition-none"
                    >
                      <span className="font-bold text-pstax-blue mr-1">{i + 1}.</span> {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Darfst du hier überhaupt arbeiten? */}
            <h2 id="aufenthalt" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Darfst du hier überhaupt arbeiten?
            </h2>
            <p className="mb-4 leading-relaxed">
              Bevor du irgendetwas anmeldest, muss eine Frage geklärt sein — und genau die wird gern übersprungen,
              weil sie sich nicht wie eine Geschäftsfrage anfühlt.
            </p>
            <p className="mb-4 leading-relaxed">
              Bist du Staatsbürger:in eines EU- oder EWR-Landes oder der Schweiz, darfst du in Österreich ohne
              besondere Erlaubnis selbstständig sein. Dann kannst du direkt zum nächsten Abschnitt springen.
            </p>
            <p className="mb-4 leading-relaxed">
              Kommst du aus einem Drittstaat, brauchst du einen Aufenthaltstitel, der Selbstständigkeit tatsächlich
              erlaubt. Es gibt mehrere — die Rot-Weiß-Rot-Karte für selbstständige Schlüsselkräfte, die
              Rot-Weiß-Rot-Karte für Gründer:innen und eine Aufenthaltsbewilligung für selbstständige
              Erwerbstätigkeit. Welche für dich infrage kommt, hängt von deinem Vorhaben ab, und die Anforderungen
              sind hoch.
            </p>

            <Callout tone="orange" label="Der teuerste Fehler">
              Ein Aufenthaltstitel, der eine Anstellung erlaubt, erlaubt nicht automatisch auch Selbstständigkeit.
              Das sind unterschiedliche Titel mit unterschiedlichen Bedingungen. Prüfe deinen, bevor du ein
              Unternehmen anmeldest — nicht danach.
            </Callout>

            <p className="mb-8 leading-relaxed">
              Das ist Aufenthaltsrecht, kein Steuerrecht, und das machen wir nicht. Die Austrian Business Agency
              führt dazu eine Übersicht —{' '}
              <ExternalLink
                href="https://www.workinaustria.com/en/residence-employment/residence-permits-for-self-employed/"
                className="text-pstax-link hover:underline"
              >
                Aufenthaltstitel für Selbstständige
              </ExternalLink>{' '}
              (auf Englisch) — und das ist der richtige Ausgangspunkt.
            </p>

            {/* Welche Form der Selbstständigkeit passt zu dir? */}
            <h2 id="welche-form" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Welche Form der Selbstständigkeit passt zu dir?
            </h2>
            <p className="mb-4 leading-relaxed">
              Österreich unterscheidet zwei Gruppen von Selbstständigen, und fast alles Weitere hängt davon ab, zu
              welcher du gehörst.
            </p>
            <ul className="mb-4 space-y-3 list-disc pl-6 leading-relaxed">
              <li>
                <strong>
                  Mit Gewerbeschein (<span lang="de">Gewerbetreibende</span>)
                </strong>{' '}
                — IT-Dienstleistungen, Fotografie, die meisten Beratungstätigkeiten, Personal Training, Handwerk. Du
                meldest ein <span lang="de">Gewerbe</span> an, wirst Mitglied der Wirtschaftskammer, und deine
                SVS-Versicherung beginnt ab demselben Tag.
              </li>
              <li>
                <strong>
                  Ohne Gewerbeschein (<span lang="de">Neue Selbständige</span>)
                </strong>{' '}
                — Schreiben, Vortragstätigkeit, Übersetzen, bestimmte therapeutische und künstlerische Tätigkeiten.
                Kein Gewerbe anzumelden, aber du musst dich selbst bei der SVS melden, und zwar erst, sobald dein
                Gewinn 6.613,20 € im Jahr 2026 übersteigt.
              </li>
            </ul>

            <Callout tone="blue" label="Wichtig">
              Das ist keine Wahl. Es ergibt sich aus der Tätigkeit selbst, und ein Fehler hier wird rückwirkend
              korrigiert. Liegt deine Tätigkeit im Graubereich, lass sie vorher prüfen — der Gründerservice der
              Wirtschaftskammer macht das kostenlos.
            </Callout>

            <p className="mb-4 leading-relaxed">
              Auch Gewerbescheine gibt es in zwei Varianten. Die meisten sind freie Gewerbe, die du sofort anmelden
              kannst. Manche sind reglementiert und brauchen einen Befähigungsnachweis. Welche Kategorie zutrifft,
              ist an der Berufsbezeichnung allein oft nicht erkennbar.
            </p>
            <p className="mb-8 leading-relaxed">
              Was die jeweilige Form für deine Sozialversicherung bedeutet, erklären wir im{' '}
              <Link to={SVS_GUIDE_PATH} className="text-pstax-link hover:underline font-semibold">
                vollständigen SVS-Guide
              </Link>
              .
            </p>

            {/* Die Anmeldungen, in der richtigen Reihenfolge */}
            <h2 id="schritte" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Die Anmeldungen, in der richtigen Reihenfolge
            </h2>
            <p className="mb-6 leading-relaxed">
              Die Reihenfolge ist wichtig — jeder Schritt liefert etwas, das der nächste braucht.
            </p>
            <ol className="mb-8 space-y-4">
              {STEPS.map((step, i) => (
                <li key={step.title} className="bg-white border border-pstax-border rounded-2xl p-5 flex gap-4">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-pstax-navy text-white font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="leading-relaxed">
                    <strong>{step.title}</strong> — {step.body}
                  </p>
                </li>
              ))}
            </ol>

            {/* Die Förderung, die die meisten Gründer verpassen */}
            <h2 id="neufoeg" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Die Förderung, die die meisten Gründer verpassen
            </h2>
            <p className="mb-4 leading-relaxed">
              Österreich erlässt echten Neugründungen bestimmte Gebühren und Abgaben nach dem{' '}
              <span lang="de">Neugründungs-Förderungsgesetz</span>, kurz NeuFöG. Das betrifft zum Beispiel
              Stempelgebühren und bundesrechtliche Verwaltungsabgaben auf die Gründungsunterlagen und — falls du
              Mitarbeiter:innen einstellst — bestimmte Lohnnebenkosten in den ersten zwölf Monaten.
            </p>
            <p className="mb-4 leading-relaxed">
              Zwei Bedingungen zählen: Es muss sich um eine echte Neugründung handeln, und du darfst in den
              vorangegangenen fünf Jahren keine vergleichbare Tätigkeit geführt haben.
            </p>

            <Callout tone="orange" label="Woran es meistens scheitert: das Timing, nicht das Formular">
              Die Befreiung wird mit einer amtlichen Bestätigung (Formular NeuFö2) geltend gemacht, und die musst du
              vorlegen, wenn du sie beanspruchst — nicht nachträglich besorgen. Hol dir die Bestätigung, bevor du
              die Anmeldungen einreichst, nicht danach.
            </Callout>

            {/* Was dein erstes Jahr wirklich kostet */}
            <h2 id="erstes-jahr" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Was dein erstes Jahr wirklich kostet
            </h2>
            <p className="mb-4 leading-relaxed">
              Zwei Rechnungen, von zwei Behörden, nach völlig unterschiedlichem Zeitplan.
            </p>
            <p className="mb-4 leading-relaxed">
              Die SVS startet sofort, wenn du einen Gewerbeschein hast — 160,81 € im Monat im Jahr 2026, selbst bei
              null Umsatz. Das wirkt überschaubar, und genau das ist das Problem: In den ersten beiden Jahren sind
              diese Beiträge nur vorläufig, und der tatsächliche Betrag wird erst Jahre später vorgeschrieben.
              Unser{' '}
              <Link to={SVS_GUIDE_PATH} className="text-pstax-link hover:underline font-semibold">
                SVS-Guide
              </Link>{' '}
              erklärt, wie das funktioniert, und der Jahresend-Check erklärt, was du davor noch tun kannst.
              <SoonBadge />
            </p>
            <p className="mb-4 leading-relaxed">
              Das Finanzamt kommt später. Im ersten Jahr zahlst du gar keine Einkommensteuer. Die Rechnung dafür
              kommt mit deinem ersten Steuerbescheid, meist zwei Jahre später — zusammen mit den Vorauszahlungen für
              das Jahr, in dem du dann gerade steckst. Mehr dazu in unserem Guide zur Einkommensteuer für
              Selbstständige.
              <SoonBadge />
            </p>
            <p className="mb-8 leading-relaxed">
              Daraus ergibt sich die nützlichste Gewohnheit, die du gleich am Anfang etablieren kannst: Leg dir etwa
              ein Drittel deines Gewinns zur Seite, auf einem eigenen Konto, und rühr es nicht an. In den ersten
              beiden Jahren legst du mehr zurück, als dir tatsächlich vorgeschrieben wird. Dieser Überschuss ist
              kein Extra-Geld — er ist die Nachzahlung, die schon wartet.
            </p>

            {/* Brauchst du eine:n Steuerberater:in? */}
            <h2 id="steuerberater" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Brauchst du eine:n Steuerberater:in?
            </h2>
            <p className="mb-4 leading-relaxed">
              Ehrlich gesagt: nein, nicht rechtlich. Viele Selbstständige in Österreich machen ihre Steuererklärung
              selbst und kommen gut zurecht — wenn deine Situation einfach ist und dein Deutsch gut ist, ist das
              eine vernünftige Entscheidung.
            </p>
            <p className="mb-4 leading-relaxed">
              Schwierig wird es an drei Stellen. Alles — die Formulare, die Online-Portale, die Schreiben — ist auf
              Deutsch, und die Fristen laufen ab dem Tag der Zustellung, nicht ab dem Tag, an dem du verstehst,
              worum es geht. Die Fachbegriffe haben keine sauberen Entsprechungen in anderen Sprachen, eine
              Übersetzung sagt dir also nicht, was zu tun ist. Und die teuren Fehler in den ersten Jahren sind
              nicht die, die dir auffallen — es sind die Freibeträge, von denen dir niemand erzählt hat, und die
              Fristen, von denen du nicht wusstest, dass sie schon liefen.
            </p>
            <p className="mb-8 leading-relaxed">
              Das ist der ehrliche Grund, jemanden an der Seite zu haben — nicht, weil es unmöglich wäre, sondern
              weil du im ersten Jahr noch nicht weißt, was du nicht weißt.
            </p>

            {/* pstax-team-group.webp/.jpg (1400×933, Foto für den Abschnitt
                „Brauchst du eine:n Steuerberater:in?") wurde in dieser Session
                nicht bereitgestellt — Slot bleibt leer statt ein anderes Foto
                einzusetzen, wie auf der englischen Schwesterseite. */}

            <PartnerCard />

            {/* FAQ */}
            <h2 id="faq" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Häufige Fragen
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS_SELBSTSTAENDIG_WERDEN.map((faq) => (
                <details key={faq.q} className="group bg-white border border-pstax-border rounded-2xl overflow-hidden">
                  <summary className="cursor-pointer list-none px-5 py-4 min-h-[44px] flex items-center justify-between gap-4 font-semibold text-pstax-navy">
                    <span>{faq.q}</span>
                    <span
                      aria-hidden="true"
                      className="shrink-0 text-pstax-blue transition-transform group-open:rotate-180 motion-reduce:transition-none"
                    >
                      ▾
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-pstax-gray leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>

            {/* Offizielle Quellen */}
            <h2 id="quellen" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Offizielle Quellen
            </h2>
            <p className="mb-4 leading-relaxed">
              Die Regeln auf dieser Seite stammen aus offiziellen österreichischen Quellen.
            </p>
            <ul className="mb-4 space-y-4 leading-relaxed">
              <li>
                <ExternalLink
                  href="https://www.workinaustria.com/en/residence-employment/residence-permits-for-self-employed/"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Work in Austria — Aufenthaltstitel für Selbstständige
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Auf Englisch. Welche Aufenthaltstitel Selbstständigkeit erlauben und was sie voraussetzen.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.wko.at/gruendung/gewerbeanmeldung"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Gewerbeanmeldung
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Wo du ein Gewerbe anmeldest, welche Unterlagen du brauchst, freie versus reglementierte Gewerbe.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.wko.at/steuern/neugruendungs-foerderungsgesetz-neugruender"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Neugründungs-Förderungsgesetz (NeuFöG)
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Was die Gründungsförderung abdeckt, die Bedingungen, und welche Bestätigung du brauchst.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.svs.at/"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  SVS — Sozialversicherungsanstalt der Selbständigen
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Anmeldung, Beitragsregeln und die Versicherungsgrenze für Neue Selbständige.
                </span>
              </li>
            </ul>

            {/* CTA */}
            <div className="mt-14 bg-pstax-navy text-white rounded-3xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">
                Nicht sicher, welcher Weg für dich passt?
              </h2>
              <p className="text-white/75 leading-relaxed mb-6 max-w-2xl">
                Buch dir ein kostenloses 30-Minuten-Gespräch. Wir klären, welche Anmeldungen du brauchst, in
                welcher Reihenfolge, und was dein erstes Jahr kosten wird. Du entscheidest danach, ob du mit uns
                zusammenarbeiten willst. Ohne Verpflichtung.
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <Link to="/de/#fit-check" className="btn-accent motion-reduce:transition-none">
                  Gratis Erstgespräch buchen
                </Link>
                <Link
                  to={SVS_GUIDE_PATH}
                  className="btn-outline !border-white !text-white hover:!bg-white/10 motion-reduce:transition-none"
                >
                  SVS-Guide lesen
                </Link>
              </div>
              <p className="text-xs text-white/50">
                Fixpreise ab 800 € pro Jahr. Über 600 Klient:innen in Österreich. Kommunikation auf Deutsch oder
                Englisch — wie du willst.
              </p>
            </div>

            {/* Autorenbox */}
            <div className="mt-10 bg-white border border-pstax-border rounded-2xl p-6 sm:p-8 flex gap-5 items-start">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt="Pascal Schraml, österreichischer Steuerberater und Gründer von PSTax."
                  width={76}
                  height={76}
                  loading="lazy"
                  decoding="async"
                  className="w-[76px] h-[76px] rounded-full object-cover"
                />
              </picture>
              <div>
                <p className="font-display font-bold text-pstax-navy text-lg mb-0.5">
                  Fachlich geprüft von Pascal Schraml
                </p>
                <p className="text-sm text-pstax-gray mb-3">Steuerberater, Gründer von PSTax</p>
                <p className="text-sm text-pstax-gray leading-relaxed">
                  Pascal berät seit 2023 Selbständige in Österreich und prüft jeden Beitrag auf dieser Seite
                  fachlich. PSTax betreut über 600 Klient:innen, mehr als die Hälfte davon ohne
                  deutsche Muttersprache.
                </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-6 bg-pstax-bg rounded-2xl border border-pstax-border">
              <p className="text-xs text-pstax-gray leading-relaxed">
                <strong className="text-pstax-navy">Zu dieser Seite.</strong> Alle Werte gelten für 2026 und ändern
                sich jährlich. Dieser Beitrag gibt allgemeine Informationen zur Selbstständigkeit in Österreich; er
                ist keine individuelle Beratung und kann sie nicht ersetzen. Fragen zu Aufenthalt und
                Beschäftigungsbewilligung sind Aufenthaltsrecht und liegen außerhalb unseres fachlichen Bereichs —
                die Quellen oben sind dafür der richtige Ausgangspunkt. Fachlich geprüft von Pascal Schraml,
                Steuerberater, PSTax Steuerberatung FlexCo.
              </p>
            </div>
          </article>
      </div>
    </div>
  );
};
